import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Loader2, Download, Copy, Save, PenLine, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function ModuleLegalDraft({ moduleKey, item, onRefresh }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(item.draft_status === "generating");
  const [doc, setDoc] = useState(item.documento || "");
  const [dirty, setDirty] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signers, setSigners] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!dirty) setDoc(item.documento || "");
    if (item.draft_status === "generating") {
      setBusy(true);
      pollRef.current = setInterval(() => onRefresh?.(), 4000);
    } else {
      setBusy(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [item.draft_status, item.documento]); // eslint-disable-line

  const gen = async () => {
    setBusy(true); setDirty(false);
    try {
      await api.post(`/mod/${moduleKey}/generate/${item.id}`);
      toast.success("Redactando documento… (puede tardar ~1 min)");
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); setBusy(false); }
  };
  const save = async () => {
    try { await api.patch(`/mod/${moduleKey}/items/${item.id}`, { documento: doc }); setDirty(false); toast.success("Documento guardado"); onRefresh?.(); }
    catch { toast.error("Error al guardar"); }
  };
  const download = async () => {
    try {
      const r = await api.get(`/mod/${moduleKey}/document/${item.id}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `${item.titulo || "documento"}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Genera el documento antes de descargar"); }
  };
  const sendToSign = async () => {
    const list = signers.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!list.length) return toast.error("Agrega al menos un firmante");
    setSending(true);
    try {
      const { data } = await api.post(`/mod/${moduleKey}/send-to-sign/${item.id}`, { signers: list });
      toast.success(`Enviado a OMNIA Sign · ${data.signers} firmante(s)`);
      setSignOpen(false); setSigners(""); onRefresh?.();
      navigate("/os/sign");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar a firma"); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-3" data-testid="module-legal-draft">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 overline text-navy/40"><FileSignature className="w-3.5 h-3.5" /> Redacción del documento</div>
        <div className="flex items-center gap-2">
          {doc && <button onClick={() => { navigator.clipboard.writeText(doc); toast.success("Copiado"); }} className="text-navy/30 hover:text-violet" title="Copiar"><Copy className="w-4 h-4" /></button>}
          {doc && <button onClick={download} className="btn btn-outline text-sm" data-testid="draft-download-btn"><Download className="w-4 h-4" /> PDF</button>}
          {doc && <button onClick={() => setSignOpen((v) => !v)} className="btn btn-outline text-sm" data-testid="draft-sign-btn"><PenLine className="w-4 h-4" /> Enviar a firma</button>}
          <button onClick={gen} disabled={busy} className="btn btn-primary text-sm" data-testid="draft-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} {doc ? "Regenerar" : "Redactar documento"}
          </button>
        </div>
      </div>
      {signOpen && doc && (
        <div className="rounded-lg border border-violet/25 bg-violet/5 p-3 space-y-2" data-testid="draft-sign-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-navy flex items-center gap-1.5"><PenLine className="w-4 h-4 text-violet" /> Enviar a OMNIA Sign</span>
            <button onClick={() => setSignOpen(false)} className="text-navy/30 hover:text-navy"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-navy/50">Firmantes (uno por línea: nombre o correo). Se generará el PDF y se creará la solicitud de firma.</p>
          <textarea value={signers} onChange={(e) => setSigners(e.target.value)} rows={3} data-testid="draft-signers-input"
            placeholder={"Juan Pérez\ncliente@empresa.com"} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm" />
          <button onClick={sendToSign} disabled={sending} className="btn btn-primary text-sm w-full justify-center" data-testid="draft-send-sign-confirm">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Crear solicitud de firma
          </button>
        </div>
      )}
      <p className="text-xs text-navy/45">La IA redactará un <b>{(item.tipo_documento || "documento").toLowerCase()}</b> para <b>{item.pais || "el país"}</b> con las partes y datos indicados. Puedes editarlo y descargarlo en PDF.</p>
      {busy && !doc && <div className="py-6 flex justify-center items-center text-navy/30"><Loader2 className="w-5 h-5 animate-spin" /> <span className="ml-2 text-sm text-navy/40">Redactando…</span></div>}
      {doc && (
        <>
          <textarea value={doc} onChange={(e) => { setDoc(e.target.value); setDirty(true); }} rows={16} data-testid="draft-editor"
            className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm font-mono leading-relaxed" />
          {dirty && <button onClick={save} className="btn btn-primary text-sm" data-testid="draft-save-btn"><Save className="w-4 h-4" /> Guardar cambios</button>}
        </>
      )}
    </div>
  );
}
